import * as THREE from "three";

/**
 * Hand-built low-poly tank (reliable + reads clearly as a tank, unlike the
 * mis-oriented FBX2glTF GLB). The "turret" child is separable so entity views
 * can rotate it independently to the aim direction.
 *
 * Body + turret use the player color; tracks / barrel / wheels are dark.
 */
export function createTankPrimitive(color: number): THREE.Group {
  const tank = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.1 });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x2b2f36, roughness: 0.8, metalness: 0.1 });
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x3a3f47, roughness: 0.9 });
  const barrelMat = new THREE.MeshStandardMaterial({ color: 0x1f2329, roughness: 0.5, metalness: 0.3 });

  // Hull (main body)
  const hull = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.7, 3.4), bodyMat);
  hull.position.y = 0.65;
  tank.add(hull);

  // Sloped front glacis
  const glacis = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.5, 0.8), bodyMat);
  glacis.position.set(0, 0.7, 1.55);
  glacis.rotation.x = -0.35;
  tank.add(glacis);

  // Tracks (two side boxes)
  const trackGeo = new THREE.BoxGeometry(0.7, 0.9, 3.8);
  for (const sx of [-1.1, 1.1]) {
    const track = new THREE.Mesh(trackGeo, darkMat);
    track.position.set(sx, 0.5, 0);
    tank.add(track);
  }
  // Road wheels (detail)
  const wheelGeo = new THREE.CylinderGeometry(0.36, 0.36, 0.35, 12);
  for (const sx of [-1.1, 1.1]) {
    for (const sz of [-1.3, -0.45, 0.45, 1.3]) {
      const w = new THREE.Mesh(wheelGeo, wheelMat);
      w.rotation.z = Math.PI / 2;
      w.position.set(sx, 0.45, sz);
      tank.add(w);
    }
  }

  // Turret group (rotates independently to the aim)
  const turret = new THREE.Group();
  turret.name = "turret";
  turret.position.set(0, 1.15, 0);

  const turretBody = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.7, 2.0), bodyMat);
  turretBody.position.y = 0;
  turret.add(turretBody);

  const hatch = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.25, 0.5), darkMat);
  hatch.position.set(0, 0.45, -0.2);
  turret.add(hatch);

  // Barrel (cylinder rotated to point +Z)
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.18, 2.6, 12), barrelMat);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.05, 1.5);
  turret.add(barrel);

  const mantlet = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.5, 0.6), barrelMat);
  mantlet.position.set(0, 0.05, 0.95);
  turret.add(mantlet);

  tank.add(turret);
  return tank;
}

/** `ricochet` picks a cool violet palette instead of the default hot amber so
 * bot_ricochet's bouncing shots read as visually distinct at a glance. */
export function createProjectilePrimitive(ricochet = false): THREE.Mesh {
  const coreColor = ricochet ? 0xd9b8ff : 0xffe28a;
  const emissiveColor = ricochet ? 0x9333ea : 0xffaa00;
  const haloColor = ricochet ? 0xb388ff : 0xffc04d;
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.26, 12, 12),
    new THREE.MeshStandardMaterial({
      color: coreColor,
      emissive: emissiveColor,
      emissiveIntensity: 2.2,
    }),
  );
  // Additive glow halo.
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(0.6, 10, 10),
    new THREE.MeshBasicMaterial({
      color: haloColor,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  core.add(halo);
  return core;
}

export function createCratePrimitive(color = 0x8d6e63): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.BoxGeometry(2, 2, 2),
    new THREE.MeshStandardMaterial({ color }),
  );
}
